/**
 * stimulus-videos: hosted stimuli for CONVERSATION + THREAD surfaces.
 *
 * Wire contract (QS / TwinChat / Home): a room/chat request may carry
 *   `stimulusVideos: Array<{ url: string; label?: string; mimeType?: string }>`
 * alongside inline stimulus images. `url` is a HOSTED https URL (our GCS hosting),
 * never base64: video bytes are far too large to ride a JSON message body, so a video
 * attaches like an image (same ChatImage list, video/* mimeType, base64 kept in memory
 * for the preview tile) but only its hosted ref ever leaves the browser.
 *
 * Thread surface (Stimuli Anywhere Step 1): images ALSO host via the same questionnaire-
 * concept upload so python can read a public URL. Use `uploadHostedChatStimulus` +
 * `hasStimulusStillHosting` there. Home / TwinChat keep `uploadHostedChatVideo` (video-only
 * refusal) and `hasVideoStillHosting` so their send path stays byte-identical.
 *
 * Quick Study has its own hosting endpoint (/quick-study/creatives); threads reuse
 * POST /files/upload/questionnaire-concept-image; conversation videos use the chat-only
 * POST /files/upload/chat-video.
 */
import type { ChatImage } from '@/lib/api'
import { filesApi } from '@/lib/api/client'
import { isVideoCreativeLike } from './CreativeMedia'
import type { UploadHosted } from './useChatFileIngest'

export interface StimulusVideo {
  /** HOSTED https URL (our GCS hosting). Never base64, never a data: URL. */
  url: string
  /** Optional user-typed name for the creative ("Version A"). */
  label?: string
  /** Container MIME (video/mp4, video/webm, video/quicktime). */
  mimeType?: string
}

/** Is this attachment a video creative? Delegates to the ONE shared predicate. Loose on
 *  mimeType (optional/null) so callers can probe partial shapes, like the predicate itself. */
export function isVideoChatImage(im: { mimeType?: string | null }): boolean {
  return isVideoCreativeLike({ mimeType: im.mimeType })
}

/**
 * Send gate: true while any attached video has no hosted url yet. Sending in that window
 * would run the turn without the stimulus the user attached (video bytes never ride the
 * payload), so composers block send and show a hint while this is true. Mirrors the
 * Quick Study `videoStillHosting` gate.
 */
export function hasVideoStillHosting(images: Array<ChatImage>): boolean {
  return images.some((im) => isVideoChatImage(im) && !im.url)
}

/**
 * Thread send/bind gate: true while any attached video OR image still lacks a hosted
 * url. Thread images must not ride inline base64 on the stimulus wire. Do not use this
 * on Home / TwinChat — those surfaces still send images as base64 and only gate video.
 */
export function hasStimulusStillHosting(images: Array<ChatImage>): boolean {
  return images.some((im) => {
    const mime = (im.mimeType ?? '').toLowerCase()
    const needsHost = mime.startsWith('video/') || mime.startsWith('image/')
    return needsHost && !im.url && !im.hostFailed
  })
}

/**
 * Map the attachment list to the wire-contract `stimulusVideos` payload. HOSTED videos
 * only: an unhosted video is excluded here (the send gate should have blocked it; the
 * caller is responsible for surfacing any leftover, never for inlining its bytes).
 */
export function toStimulusVideos(images: Array<ChatImage>): Array<StimulusVideo> {
  return images
    .filter((im) => isVideoChatImage(im) && Boolean(im.url))
    .map((im) => ({
      url: im.url as string,
      ...(im.label ? { label: im.label } : {}),
      ...(im.mimeType ? { mimeType: im.mimeType } : {}),
    }))
}

// Extension by container so the server-side byte sniff and the stored filename agree.
const STIMULUS_EXTENSION_BY_MIME: Record<string, string> = {
  'video/mp4': 'mp4',
  'video/webm': 'webm',
  'video/quicktime': 'mov',
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/webp': 'webp',
  'image/gif': 'gif',
}

function decodeBase64ToFile(base64: string, mimeType: string, filename: string): File {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return new File([bytes], filename, { type: mimeType })
}

/** Storage basename only: strip path separators. Empty / `.` / `..` fall back. */
export function sanitizeStorageFilename(filename: string | undefined, fallback: string): string {
  const leaf = (filename ?? '').replace(/\\/g, '/').split('/').filter(Boolean).pop() ?? ''
  const trimmed = leaf.trim()
  if (!trimmed || trimmed === '.' || trimmed === '..') {
    return fallback
  }
  return trimmed
}

function fallbackStimulusFilename(mimeType: string): string {
  const normalized = (mimeType ?? '').toLowerCase().split(';')[0].trim()
  const isVideo = normalized.startsWith('video/')
  const ext = STIMULUS_EXTENSION_BY_MIME[normalized] ?? (isVideo ? 'mp4' : 'bin')
  return isVideo ? `chat-video.${ext}` : `chat-image.${ext}`
}

/**
 * Thread-surface hosting uploader: image/* and video/* both POST to
 * /files/upload/questionnaire-concept-image so the tile carries a public URL + files.token.
 */
export const uploadHostedChatStimulus: UploadHosted = async ({ base64, mimeType, filename }) => {
  const normalized = (mimeType ?? '').toLowerCase().split(';')[0].trim()
  const isVideo = normalized.startsWith('video/')
  const isImage = normalized.startsWith('image/')
  if (!isVideo && !isImage) {
    throw new Error('thread stimulus hosting accepts image/* and video/* only')
  }
  const storageName = sanitizeStorageFilename(filename, fallbackStimulusFilename(mimeType))
  const file = decodeBase64ToFile(base64, mimeType, storageName)
  const uploaded = await filesApi.uploadQuestionnaireConceptImage(file)
  const objectKey = stimulusStorageObjectKey(uploaded.filePath)
  return {
    url: uploaded.publicUrl,
    ...(objectKey ? { objectKey } : {}),
    fileId: uploaded.token,
  }
}

/** Prefer a storage key. Public https filePath values are not object keys. */
function stimulusStorageObjectKey(filePath: string | undefined): string | undefined {
  const key = (filePath ?? '').trim()
  if (!key || /^https?:\/\//i.test(key)) {
    return undefined
  }
  return key
}

/**
 * Conversation-surface hosting uploader (Home / TwinChat / room chat).
 *
 * VIDEO-ONLY on purpose: the ingest hook hosts every attachment when an uploader is
 * wired, but conversation images ride inline (base64) exactly as before, so hosting them
 * would only pollute storage with refs the send path strips anyway. A non-video call
 * therefore resolves `null` WITHOUT touching the network, so the hook keeps the base64-only
 * image unchanged (no failure toast, no hostFailed). Videos go to the chat-only video
 * route; the return is `{url, objectKey}` only so Home/TwinChat stay identical.
 */
export const uploadHostedChatVideo: UploadHosted = async ({ base64, mimeType, filename }) => {
  const normalized = (mimeType ?? '').toLowerCase().split(';')[0].trim()
  if (!normalized.startsWith('video/')) {
    return null
  }
  const storageName = sanitizeStorageFilename(filename, fallbackStimulusFilename(mimeType))
  const uploaded = await filesApi.uploadChatVideo(decodeBase64ToFile(base64, mimeType, storageName))
  const objectKey = stimulusStorageObjectKey(uploaded.filePath)
  return {
    url: uploaded.publicUrl,
    ...(objectKey ? { objectKey } : {}),
  }
}
